import { useCallback, useState, type ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { ConnectedAppsProvider, type ConnectableProvider } from '@cowbell/moo';
import {
  useConnectableProviders,
  useSetConnectionEnabled,
  useStartConnectionAuthorize,
} from '../hooks/useUserConnections';
import { ProviderIcon } from '../components/ProviderIcon';
import { useActor } from '../../hooks/useActor';
import { currentLocationPath, rememberStudioReturnPath } from './studio-return';
import { MooSettingsModal } from './settings/MooSettingsModal';

/**
 * Supplies the composer's connected-apps chip with data and routing.
 *
 * `libs/moo` cannot import from `apps/console` (it is a buildable Nx lib), and
 * the chip renders deep inside `ChatPanel` where the host cannot pass props.
 * So the console owns the React Query hooks and react-router here, and hands
 * them down through context — the same shape `SourcesProvider` uses.
 */
export function ConnectedAppsBridge({
  children,
}: {
  readonly children: ReactNode;
}) {
  const { data: providers } = useConnectableProviders();
  const { mutate: setEnabled } = useSetConnectionEnabled();
  const { mutate: startAuthorize } = useStartConnectionAuthorize();
  // Web search is not an app the user connects; it is a per-turn switch the
  // backend honours for internal agents only. Brokers, policyholders and
  // carriers never get the tool, so they never see the row either.
  const webSearchAvailable = useActor().isCowbell;

  // "Connect more" opens My Apps in a modal rather than navigating: the
  // popover only has room for a compact list, and pulling the user out of
  // their conversation to manage a connection loses their place.
  //
  // It must never route to Moo Studio -> Integrations, which is root:root only
  // (OMNI-382) and is tenant configuration rather than the user's own
  // connections.
  const history = useHistory();
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const handleManage = useCallback(() => setAppsModalOpen(true), []);

  const handleSetEnabled = useCallback(
    (provider: string, enabled: boolean) => setEnabled({ provider, enabled }),
    [setEnabled]
  );

  // Row-level Connect/Reconnect must go straight to the vendor's consent
  // screen, not through the My Apps modal — that detour is for "Connect
  // more"/"Discover more apps", which have no single provider in mind.
  const handleConnect = useCallback(
    (provider: string) => startAuthorize(provider),
    [startAuthorize]
  );

  // Gives the chip the same logo_url -> bundled asset -> generic icon fallback
  // as every other surface; catalog logo_url is frequently empty.
  const renderIcon = useCallback(
    ({ provider, size }: { provider: ConnectableProvider; size: number }) => (
      <ProviderIcon
        provider={provider.provider}
        logoUrl={provider.logoUrl}
        displayName={provider.displayName}
        size={size}
      />
    ),
    []
  );

  return (
    <ConnectedAppsProvider
      providers={providers ?? []}
      renderIcon={renderIcon}
      onManage={handleManage}
      onConnect={handleConnect}
      onSetEnabled={handleSetEnabled}
      webSearchAvailable={webSearchAvailable}
    >
      {children}
      <MooSettingsModal
        opened={appsModalOpen}
        onClose={() => setAppsModalOpen(false)}
        initialPane="apps"
        onNavigate={(path) => {
          setAppsModalOpen(false);
          // Record where we came from before leaving. Studio's "Back to Ask
          // Moo" reads this one-shot value; the Ask Moo -> Studio handoff
          // always sets it, and links from the Studio card were the one path
          // that skipped it — so returning dropped the user on the persona
          // home instead of the conversation they left.
          rememberStudioReturnPath(currentLocationPath());
          history.push(path);
        }}
      />
    </ConnectedAppsProvider>
  );
}
